<template>
  <div class="flex flex-col gap-6">
    <div v-if="!lockedClientId" class="w-full">
      <UFormField label="Client" description="Point code validation is client-specific." required class="w-full">
        <UIClientpicker class="w-full" />
      </UFormField>
    </div>

    <div v-else-if="clientLabel" class="text-sm text-gray-500">
      Client: <span class="font-medium text-gray-900 dark:text-gray-100">{{ clientLabel }}</span>
    </div>

    <UAlert
      v-if="!effectiveClientId"
      color="warning"
      variant="soft"
      title="Select a client"
      description="Choose a client before validating a point code."
      icon="i-heroicons-information-circle-20-solid"
    />

    <div class="flex flex-col gap-4" :class="{ 'opacity-60 pointer-events-none': !effectiveClientId }">
      <div class="flex items-center gap-2 flex-wrap" @paste="onPaste">
        <UPinInput
          v-model="pinValue2"
          :length="3"
          size="lg"
          variant="subtle"
          :color="inputColor"
          :highlight="hasValidated"
          :disabled="loading || !effectiveClientId"
        />
        <span class="text-xl font-bold text-gray-400">-</span>
        <UPinInput
          v-model="pinValue3"
          :length="3"
          size="lg"
          variant="subtle"
          :color="inputColor"
          :highlight="hasValidated"
          :disabled="loading || !effectiveClientId"
        />
        <span class="text-xl font-bold text-gray-400">-</span>
        <UPinInput
          v-model="pinValue4"
          :length="3"
          size="lg"
          variant="subtle"
          :color="inputColor"
          :highlight="hasValidated"
          :disabled="loading || !effectiveClientId"
          @complete="validateCode"
        />
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <UButton
          :loading="loading"
          :disabled="!isComplete || !effectiveClientId"
          color="primary"
          icon="i-heroicons-check-circle"
          label="Validate"
          @click="validateCode"
        />
        <UButton
          v-if="hasValidated || isComplete"
          variant="ghost"
          color="neutral"
          icon="i-heroicons-arrow-path"
          label="Reset"
          @click="resetForm"
        />
      </div>

      <div class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 px-4 py-3">
        <div class="min-w-0">
          <p class="text-sm font-medium text-gray-900 dark:text-white">Report without validating</p>
          <p class="text-xs text-gray-500">Use when the point code is missing or you need to log suspicious activity manually.</p>
        </div>
        <UButton
          variant="outline"
          color="warning"
          icon="i-heroicons-flag-20-solid"
          label="Add report manually"
          :disabled="!effectiveClientId"
          @click="openManualReport"
        />
      </div>
    </div>

    <UAlert
      v-if="error"
      color="error"
      variant="soft"
      :title="error"
      icon="i-heroicons-exclamation-circle-20-solid"
    />

    <template v-if="validationResult">
      <UAlert
        :color="resultColor"
        variant="soft"
        :title="validationResult.message"
        :description="`Point code: ${validationResult.pointCode}`"
        :icon="resultIcon"
      />

      <div v-if="primaryUsage" class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div class="min-w-0">
            <p class="text-xs font-medium uppercase tracking-wide text-gray-500">Used by</p>
            <NuxtLink
              v-if="effectiveClientId"
              :to="`/dashboard/webusers/${effectiveClientId}/${primaryUsage.webUserId}`"
              class="text-base font-semibold text-primary hover:underline inline-flex items-center gap-1"
              target="_blank"
            >
              {{ primaryUsage.name }}
              <UIcon name="i-heroicons-arrow-top-right-on-square" class="w-4 h-4" />
            </NuxtLink>
            <p class="text-sm text-gray-500 truncate">{{ primaryUsage.email }}</p>
            <p v-if="isSameAsContextUser" class="text-xs text-warning mt-1">Same as the customer on this page</p>
          </div>

          <div class="flex flex-wrap gap-2">
            <UBadge variant="subtle" color="warning">{{ primaryUsage.type }}</UBadge>
            <UBadge variant="subtle" color="neutral">{{ primaryUsage.points }} pts</UBadge>
            <UBadge v-if="primaryUsage.trxDate" variant="subtle" color="neutral">{{ primaryUsage.trxDate }}</UBadge>
          </div>
        </div>

        <div v-if="customerSummaryLoading" class="p-6 flex items-center justify-center gap-2 text-sm text-gray-500">
          <UIcon name="i-heroicons-arrow-path-20-solid" class="w-5 h-5 animate-spin" />
          Loading customer data...
        </div>

        <div v-else-if="customerSummary" class="p-4 space-y-4">
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div class="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p class="text-xs text-gray-500">Balance</p>
              <p class="text-lg font-semibold text-gray-900 dark:text-white">{{ customerSummary.currentBalance }}</p>
            </div>
            <div class="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p class="text-xs text-gray-500">Transactions</p>
              <p class="text-lg font-semibold text-gray-900 dark:text-white">{{ customerSummary.totalTransactions }}</p>
            </div>
            <div class="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p class="text-xs text-gray-500">Earned</p>
              <p class="text-lg font-semibold text-green-600">{{ customerSummary.earnedCount }}</p>
            </div>
            <div class="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p class="text-xs text-gray-500">Redeemed</p>
              <p class="text-lg font-semibold text-red-500">{{ customerSummary.redeemedCount }}</p>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div v-if="customerSummary.customer.totalFailedPointCodeAttempts > 0" class="flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <UIcon name="i-heroicons-shield-exclamation" class="w-4 h-4" />
              {{ customerSummary.customer.totalFailedPointCodeAttempts }} failed point code attempts
            </div>
            <div v-if="customerSummary.customer.pointshopLocked" class="flex items-center gap-2 text-red-600">
              <UIcon name="i-heroicons-lock-closed" class="w-4 h-4" />
              Pointshop locked
            </div>
          </div>

          <div v-if="customerSummary.recentTransactions.length" class="space-y-2">
            <p class="text-xs font-medium uppercase tracking-wide text-gray-500">Recent point activity</p>
            <div class="space-y-2">
              <div
                v-for="point in customerSummary.recentTransactions"
                :key="point.id"
                class="flex items-center justify-between gap-3 rounded-lg border border-gray-100 dark:border-gray-800 px-3 py-2 text-sm"
              >
                <div class="min-w-0">
                  <p class="font-medium text-gray-900 dark:text-white capitalize">{{ point.type }}</p>
                  <p class="text-xs text-gray-500 truncate">{{ point.additionalInfo || '—' }}</p>
                </div>
                <div class="text-right shrink-0">
                  <p :class="point.points >= 0 ? 'text-green-600' : 'text-red-500'" class="font-semibold">
                    {{ point.points >= 0 ? '+' : '' }}{{ point.points }}
                  </p>
                  <p class="text-xs text-gray-500">{{ formatTrxDate(point.dateTrx) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 flex justify-end">
          <UButton
            icon="i-heroicons-flag-20-solid"
            label="Flag suspicious code"
            color="warning"
            variant="soft"
            @click="openFlagModal"
          />
        </div>
      </div>

      <div
        v-else-if="validationResult.status !== 'Valid'"
        class="flex justify-end"
      >
        <UButton
          icon="i-heroicons-flag-20-solid"
          label="Flag suspicious code"
          color="warning"
          variant="soft"
          @click="openFlagModal"
        />
      </div>
    </template>

    <WebuserFailedPointCodeModal
      v-if="effectiveClientId"
      v-model:open="showReportModal"
      :client-id="effectiveClientId"
      :wu-id="reportTarget.wuId"
      :customer-name="reportTarget.name"
      :customer-email="reportTarget.email"
      :initial-report="reportInitialValues"
      @success="emit('reported')"
    />
  </div>
</template>

<script lang="ts" setup>
interface Props {
  clientId?: number
  contextUserEmail?: string
  contextWuId?: number
  contextCustomerName?: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  reported: []
}>()

const userStore = useUserStore()
const { selectedClient, accessLevel } = storeToRefs(userStore)

const lockedClientId = computed(() => props.clientId ?? null)
const effectiveClientId = computed(() => lockedClientId.value ?? selectedClient.value?.id ?? null)

const clientLabel = computed(() => {
  if (!lockedClientId.value) return ''
  return accessLevel.value.find(client => client.client_id === lockedClientId.value)?.name ?? `Client ${lockedClientId.value}`
})

const {
  pinValue2,
  pinValue3,
  pinValue4,
  loading,
  validationResult,
  error,
  hasValidated,
  isComplete,
  inputColor,
  primaryUsage,
  customerSummary,
  customerSummaryLoading,
  suggestedReportReason,
  validateCode,
  resetForm,
  applyPastedCode,
  fullCode,
} = usePointCodeValidation(() => effectiveClientId.value)

const showReportModal = ref(false)
const reportMode = ref<'manual' | 'validation'>('manual')

const isSameAsContextUser = computed(() =>
  !!props.contextUserEmail &&
  !!primaryUsage.value?.email &&
  props.contextUserEmail.toLowerCase() === primaryUsage.value.email.toLowerCase()
)

const resultColor = computed(() => {
  const status = validationResult.value?.status
  if (status === 'Valid') return 'success'
  if (status === 'Invalid') return 'error'
  return 'warning'
})

const resultIcon = computed(() => {
  const status = validationResult.value?.status
  if (status === 'Valid') return 'i-heroicons-check-circle-20-solid'
  if (status === 'Invalid') return 'i-heroicons-x-circle-20-solid'
  return 'i-heroicons-exclamation-triangle-20-solid'
})

const flagTarget = computed(() => {
  if (primaryUsage.value) {
    return {
      wuId: primaryUsage.value.webUserId,
      name: primaryUsage.value.name,
      email: primaryUsage.value.email,
    }
  }

  if (props.contextUserEmail && customerSummary.value) {
    return {
      wuId: customerSummary.value.customer.wuId,
      name: customerSummary.value.customer.name,
      email: customerSummary.value.customer.email,
    }
  }

  return null
})

const flagInitialReport = computed(() => ({
  failedPointCode: fullCode.value || validationResult.value?.pointCode || '',
  failedPointCodeReason: suggestedReportReason.value,
}))

const contextCustomer = computed(() => {
  if (!props.contextWuId) return null
  return {
    wuId: props.contextWuId,
    name: props.contextCustomerName ?? '',
    email: props.contextUserEmail ?? '',
  }
})

const reportTarget = computed(() => {
  if (reportMode.value === 'validation' && flagTarget.value) {
    return flagTarget.value
  }
  if (contextCustomer.value) {
    return contextCustomer.value
  }
  return { wuId: 0, name: '', email: '' }
})

const reportInitialValues = computed(() => {
  if (reportMode.value === 'validation') {
    return flagInitialReport.value
  }

  return {
    failedPointCode: fullCode.value || undefined,
  }
})

const onPaste = async (event: ClipboardEvent) => {
  event.preventDefault()
  applyPastedCode(event.clipboardData?.getData('text') || '')
  await nextTick()
  if (isComplete.value) validateCode()
}

const uppercasePinGroup = (group: Ref<string[]>) => {
  watch(group, (value) => {
    const uppercased = value.map(char => char.toUpperCase())
    if (uppercased.join('') !== value.join('')) {
      group.value = uppercased
    }
  }, { deep: true })
}

uppercasePinGroup(pinValue2)
uppercasePinGroup(pinValue3)
uppercasePinGroup(pinValue4)

const formatTrxDate = (dateString: string) => {
  if (!dateString) return '—'
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

const openManualReport = () => {
  reportMode.value = 'manual'
  showReportModal.value = true
}

const openFlagModal = () => {
  reportMode.value = 'validation'
  showReportModal.value = true
}
</script>
